import {
  APIAdminOrganizationAttributes,
  APICurrentUser,
  APIPublicOrganizationAttributes,
  PersonalSetting,
} from '@ardoq/api-types';
import { organizationApi, userApi } from '@ardoq/api';
import { userOperations } from '@ardoq/core';
import {
  changeLanguage,
  DEFAULT_ARDOQ_I18NLOCALE,
  type I18nLocale,
} from '@ardoq/i18n';
import { isArdoqError } from '@ardoq/common-helpers';
import { logError } from '@ardoq/logging';
import {
  collectRoutines,
  dispatchAction,
  extractPayload,
  ofType,
  routine,
} from '@ardoq/rxbeach';
import {
  catchError,
  combineLatest,
  defer,
  distinctUntilChanged,
  EMPTY,
  from,
  map,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { loadedCurrentUser$ } from './currentUser/currentUser$';
import { currentOrganization$ } from './organizations/currentOrganization$';
import {
  setI18nLanguage,
  setI18nLocale,
  setTranslationBundle,
} from './i18nLocale$';
import { writePreAppLanguageCookie } from '../preAppLanguageCookie';
import { DEFAULT_LANGUAGE } from '../supportedLanguages';
import { isDevOrStagingOrCloudPreviewEnvironment } from '../env';
import { languageOverride$, type LanguageOverride } from './languageOverride$';

/**
 * The translation bundles an entry ships. Entries pass their own so a narrow
 * one (e.g. the assistant page) does not bundle the whole app's text.
 */
export type TranslationBundles = Record<string, I18nLocale>;

const getTranslationBundle = (
  bundles: TranslationBundles,
  language: string
): I18nLocale => bundles[language] ?? DEFAULT_ARDOQ_I18NLOCALE;

export const resolveLanguage = (
  user: APICurrentUser,
  organization:
    | APIAdminOrganizationAttributes
    | APIPublicOrganizationAttributes
    | undefined
): string =>
  (userOperations.getPersonalSetting(user, PersonalSetting.LANGUAGE) ||
    undefined) ??
  organization?.settings.language ??
  DEFAULT_LANGUAGE;

export const applyLanguageOverride = (
  override: LanguageOverride,
  resolvedLanguage: string,
  isOverrideAllowed: boolean
): string => (isOverrideAllowed && override) || resolvedLanguage;

const buildTranslationSyncRoutine = (bundles: TranslationBundles) =>
  combineLatest([
    loadedCurrentUser$,
    // `defer` reads `currentOrganization$` at subscribe-time rather than
    // module-eval-time. The two modules form an import cycle, so under some entry
    // orders this binding is still in its TDZ (undefined) when this routine is
    // constructed; deferring the reference sidesteps the order dependency without
    // changing runtime behaviour (the stream is subscribed long after load).
    defer(() => currentOrganization$).pipe(
      map(org => (isArdoqError(org) ? undefined : org)),
      startWith(undefined)
    ),
    languageOverride$,
  ]).pipe(
    map(([user, organization, override]) =>
      applyLanguageOverride(
        override,
        resolveLanguage(user, organization),
        isDevOrStagingOrCloudPreviewEnvironment()
      )
    ),
    distinctUntilChanged(),
    switchMap(changeLanguage),
    tap(language => {
      dispatchAction(
        setTranslationBundle(getTranslationBundle(bundles, language))
      );
      dispatchAction(setI18nLanguage(language));
      writePreAppLanguageCookie(language);
    })
  );

/**
 * Calls `changeLanguage` and swallows rejection by logging instead of
 * rethrowing, so an awaiting caller is never derailed by an unhandled
 * rejection. Plain async keeps it testable without the global rxbeach
 * `action$`.
 *
 * `changeLanguage` returns a Promise that can reject (e.g. if i18next is not
 * yet initialised when the parent pushes the locale across the bridge). If
 * that rejection propagated out of the routine, `subscribeRoutine` would
 * recover by synchronously re-subscribing — which on a shared hot action$
 * can re-trigger the same failure path and produce a "Maximum call stack
 * size exceeded" RangeError (ARD-31668).
 */
export const safelyChangeLanguage = async (
  language: string,
  _changeLanguage: typeof changeLanguage = changeLanguage,
  _logError: typeof logError = logError
): Promise<void> => {
  try {
    await _changeLanguage(language);
  } catch (error) {
    _logError(
      error instanceof Error ? error : new Error(String(error)),
      'safelyChangeLanguage: changeLanguage failed'
    );
  }
};

const ORG_FETCH_TIMEOUT_MS = 3000;

const fetchOrganizationWithinTimeout = async (
  organizationId: string
): Promise<
  APIAdminOrganizationAttributes | APIPublicOrganizationAttributes | undefined
> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<undefined>(resolve => {
    timer = setTimeout(() => resolve(undefined), ORG_FETCH_TIMEOUT_MS);
  });
  try {
    const organizationResult = await Promise.race([
      organizationApi.fetch(organizationId),
      timeout,
    ]);
    return !organizationResult || isArdoqError(organizationResult)
      ? undefined
      : organizationResult;
  } finally {
    clearTimeout(timer);
  }
};

/**
 * Resolves the translation bundle for screens rendered before
 * `stateStreamRegistry.startReducing()` connects `i18nLocale$` — the bootstrap
 * error screens (the contributor early-exit and the data-load catch). Those
 * sites cannot read `i18nLocale$.state`, which still holds the English default,
 * so the language is resolved directly from the user, `@ardoq/i18n` is switched,
 * and the matching bundle is returned.
 *
 * The org default is only fetched when the user has no personal language, and
 * that fetch is bounded by `ORG_FETCH_TIMEOUT_MS`: these screens render only
 * after this resolves, so a hung request must not leave the user on a blank
 * page. On timeout the language falls back to the default resolution.
 */
export const resolvePreStreamI18nLocale = async (
  user: APICurrentUser,
  bundles: TranslationBundles
): Promise<I18nLocale> => {
  const personalLanguage =
    userOperations.getPersonalSetting(user, PersonalSetting.LANGUAGE) ||
    undefined;
  const organization = personalLanguage
    ? undefined
    : await fetchOrganizationWithinTimeout(user.organization._id);
  const language = resolveLanguage(user, organization);
  await safelyChangeLanguage(language);
  return getTranslationBundle(bundles, language);
};

const USER_FETCH_TIMEOUT_MS = 3000;

const fetchCurrentUserWithinTimeout = async (): Promise<
  APICurrentUser | undefined
> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<undefined>(resolve => {
    timer = setTimeout(() => resolve(undefined), USER_FETCH_TIMEOUT_MS);
  });
  try {
    const userResult = await Promise.race([
      userApi.fetchCurrentUser(),
      timeout,
    ]);
    return !userResult || isArdoqError(userResult) ? undefined : userResult;
  } finally {
    clearTimeout(timer);
  }
};

/**
 * Resolves the pre-stream translation bundle for the bootstrap error screens
 * that have no user in hand (the forbidden / not-found / generic branches).
 * The current user is fetched — bounded by `USER_FETCH_TIMEOUT_MS` so a hung
 * request can never leave the screen blank — and the locale is resolved from
 * it. When the user can't be fetched the default bundle is returned, which is
 * the same English default `i18nLocale$` still holds at this point.
 */
export const resolveErrorScreenI18nLocale = async (
  bundles: TranslationBundles
): Promise<I18nLocale> => {
  const user = await fetchCurrentUserWithinTimeout();
  if (!user) {
    return getTranslationBundle(bundles, DEFAULT_LANGUAGE);
  }
  return resolvePreStreamI18nLocale(user, bundles);
};

// NOTE: `setI18nLocale` is intended to be dispatched only on popout build
// targets that receive their locale over the postMessageBridge (e.g. the
// perspective editor popout). Do NOT dispatch this action in the main app,
// gridEditor, or presentation viewer — those derive `i18nLocale$` from
// `loadedCurrentUser$` via `translationSyncRoutine`, and dispatching
// `setI18nLocale` there would overwrite the canonical locale state.
const buildSetI18nLocaleRoutine = (bundles: TranslationBundles) =>
  routine(
    ofType(setI18nLocale),
    extractPayload(),
    map(({ language }) => language),
    distinctUntilChanged(),
    switchMap(language =>
      from(safelyChangeLanguage(language)).pipe(
        map(() => language),
        catchError(() => EMPTY)
      )
    ),
    tap(language => {
      dispatchAction(
        setTranslationBundle(getTranslationBundle(bundles, language))
      );
    })
  );

export const createTranslationRoutines = (bundles: TranslationBundles) =>
  collectRoutines(buildTranslationSyncRoutine(bundles));

/**
 * Routines for build targets that receive the locale over the
 * postMessageBridge instead of subscribing to `loadedCurrentUser$` directly
 * (e.g. the perspective editor popout). Do not include this in the main app or
 * gridEditor — they use the default `translationRoutines` export.
 */
export const createBridgeTranslationRoutines = (bundles: TranslationBundles) =>
  collectRoutines(buildSetI18nLocaleRoutine(bundles));
